import React, { useState, useRef, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import {
  ArrowLeft,
  Clock,
  User,
  Package,
  Truck,
  MessageCircle,
  Paperclip,
  Star,
  Edit3,
  UserPlus,
  CheckCircle,
  XCircle,
  RotateCcw,
  Send,
  Upload,
  Download,
  Eye,
  Shield,
  AlertTriangle,
} from "lucide-react";

// UI Components
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@shared/components/ui/card";
import { Badge } from "@shared/components/ui/badge";
import { Button } from "@shared/components/ui/button";
import { Textarea } from "@shared/components/ui/textarea";
import { Avatar, AvatarFallback } from "@shared/components/ui/avatar";
import { Separator } from "@shared/components/ui/separator";
import { Input } from "@shared/components/ui/input";
import { Label } from "@shared/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@shared/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@shared/components/ui/alert-dialog";

// Hooks and Services
import {
  useTicketByIdQuery,
  useAddMessageMutation,
  useAddFeedbackMutation,
} from "./hooks/useTickets.js";
import { ticketService } from "@features/tickets/services/ticketService";
import { useAuth } from "@features/auth/contexts/AuthContext";
import HeadLayout from "@shared/layouts/HeadLayout";
import ThreeDotLoader from "@shared/components/ThreeDotLoader";
import TicketManagementActions from "./TicketManagementActions";

// Constants
import {
  TICKET_STATUS_COLORS,
  TICKET_CATEGORY_COLORS,
} from "@shared/utils/constants";

// Helper function to get user initials
const getInitials = (fullName, email) => {
  if (fullName) {
    return fullName
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  }
  if (email) {
    return email.substring(0, 2).toUpperCase();
  }
  return "UN";
};

// Message Component
const MessageCard = ({ message, isCurrentUser, onDownloadAttachment }) => {
  const { t } = useTranslation();

  return (
    <div
      className={`flex gap-3 ${
        isCurrentUser ? "flex-row-reverse" : "flex-row"
      } mb-4`}
    >
      <Avatar className="h-8 w-8 flex-shrink-0">
        <AvatarFallback className="text-xs">
          {getInitials(message.sender?.fullName, message.sender?.email)}
        </AvatarFallback>
      </Avatar>

      <div
        className={`max-w-[70%] ${isCurrentUser ? "text-right" : "text-left"}`}
      >
        <div className="flex items-center gap-2 mb-1">
          <span className="text-sm font-medium">
            {message.sender?.fullName ||
              message.sender?.email ||
              t("common.unknown", "Unknown")}
          </span>
          <Badge variant="secondary" className="text-xs">
            {t(`role.${message.sender?.role}`, message.sender?.role)}
          </Badge>
          {message.isInternal && (
            <Badge variant="destructive" className="text-xs">
              <Shield className="h-3 w-3 mr-1" />
              {t("tickets.internal", "Internal")}
            </Badge>
          )}
        </div>

        <div
          className={`rounded-lg p-3 ${
            isCurrentUser ? "bg-primary text-primary-foreground" : "bg-muted"
          }`}
        >
          <p className="text-sm whitespace-pre-wrap">{message.message}</p>

          {/* Attachments */}
          {message.attachments && message.attachments.length > 0 && (
            <div className="mt-3 pt-3 border-t border-opacity-20">
              <div className="space-y-2">
                {message.attachments.map((attachment, index) => (
                  <div key={index} className="flex items-center gap-2 text-xs">
                    <Paperclip className="h-3 w-3" />
                    <span className="flex-1 truncate">
                      {attachment.originalName}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0"
                      onClick={() => onDownloadAttachment(message._id, index)}
                    >
                      <Download className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="text-xs text-muted-foreground mt-1">
          {format(new Date(message.createdAt), "MMM dd, yyyy HH:mm")}
        </div>
      </div>
    </div>
  );
};

// Feedback Component
const FeedbackDialog = ({ ticket, onSubmitFeedback, isSubmitting }) => {
  const { t } = useTranslation();
  const [rating, setRating] = useState(5);
  const [feedback, setFeedback] = useState("");
  const [open, setOpen] = useState(false);

  const handleSubmit = async () => {
    await onSubmitFeedback(rating, feedback);
    setOpen(false);
    setFeedback("");
    setRating(5);
  };

  if (ticket.customerFeedback) {
    return (
      <Card className="border-l-4 border-l-blue-500">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Star className="h-5 w-5 text-yellow-500" />
            {t("tickets.customerFeedback", "Customer Feedback")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">
                {t("tickets.rating", "Rating")}:
              </span>
              <div className="flex">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    className={`h-4 w-4 ${
                      star <= ticket.customerFeedback.rating
                        ? "text-yellow-500 fill-current"
                        : "text-gray-300"
                    }`}
                  />
                ))}
              </div>
              <span className="text-sm text-muted-foreground">
                ({ticket.customerFeedback.rating}/5)
              </span>
            </div>
            {ticket.customerFeedback.feedback && (
              <div>
                <span className="text-sm font-medium">
                  {t("tickets.feedback", "Feedback")}:
                </span>
                <p className="text-sm text-muted-foreground mt-1">
                  {ticket.customerFeedback.feedback}
                </p>
              </div>
            )}
            <div className="text-xs text-muted-foreground">
              {t("tickets.submittedAt", "Submitted at")}:{" "}
              {format(
                new Date(ticket.customerFeedback.submittedAt),
                "MMM dd, yyyy HH:mm"
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="w-full">
          <Star className="h-4 w-4 mr-2" />
          {t("tickets.provideFeedback", "Provide Feedback")}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t("tickets.provideFeedback", "Provide Feedback")}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>{t("tickets.rating", "Rating")}</Label>
            <div className="flex gap-1 mt-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <Button
                  key={star}
                  variant="ghost"
                  size="sm"
                  className="p-1"
                  onClick={() => setRating(star)}
                >
                  <Star
                    className={`h-6 w-6 ${
                      star <= rating
                        ? "text-yellow-500 fill-current"
                        : "text-gray-300"
                    }`}
                  />
                </Button>
              ))}
            </div>
          </div>
          <div>
            <Label htmlFor="feedback">
              {t("tickets.feedback", "Feedback")} (
              {t("common.optional", "Optional")})
            </Label>
            <Textarea
              id="feedback"
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder={t(
                "tickets.feedbackPlaceholder",
                "Tell us about your experience..."
              )}
              className="mt-2"
            />
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setOpen(false)}>
              {t("common.cancel", "Cancel")}
            </Button>
            <Button onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? <ThreeDotLoader /> : t("common.submit", "Submit")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const TicketDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, isAdmin, isManagerOrAdmin } = useAuth();

  // State
  const [newMessage, setNewMessage] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [isInternal, setIsInternal] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  // Queries and Mutations
  const {
    data: ticketData,
    isLoading,
    error,
    refetch,
  } = useTicketByIdQuery(id);
  const addMessageMutation = useAddMessageMutation();
  const addFeedbackMutation = useAddFeedbackMutation();

  const ticket = ticketData?.data;

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [ticket?.messages]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <ThreeDotLoader />
      </div>
    );
  }

  if (error || !ticket) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="p-6 text-center">
            <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">
              {t("tickets.notFound", "Ticket Not Found")}
            </h2>
            <p className="text-muted-foreground mb-4">
              {t(
                "tickets.notFoundDescription",
                "The ticket you are looking for does not exist or you do not have permission to view it."
              )}
            </p>
            <Button onClick={() => navigate("/tickets")}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t("tickets.backToList", "Back to Tickets")}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isTicketOwner = ticket.creator._id === user._id;
  const canManageTicket = isManagerOrAdmin;
  const canAddInternalNote = isManagerOrAdmin;
  const canProvideFeedback = isTicketOwner && ticket.status === "resolved";

  const getStatusColor = (status) => {
    return (
      TICKET_STATUS_COLORS[status] ||
      "bg-gray-100 text-gray-800 border-gray-200"
    );
  };

  const getCategoryColor = (category) => {
    return (
      TICKET_CATEGORY_COLORS[category] ||
      "bg-gray-100 text-gray-800 border-gray-200"
    );
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setAttachments((prev) => [...prev, ...files]);
  };

  const removeAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() && attachments.length === 0) return;

    const messageData = {
      message: newMessage.trim(),
      isInternal: isInternal && canAddInternalNote,
      attachments,
    };

    await addMessageMutation.mutateAsync({ ticketId: id, messageData });

    setNewMessage("");
    setAttachments([]);
    setIsInternal(false);
  };

  const handleDownloadAttachment = async (messageId, attachmentIndex) => {
    try {
      const response = await ticketService.downloadAttachment(
        id,
        messageId,
        attachmentIndex
      );
      // Handle file download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "attachment");
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  const handleProvideFeedback = async (rating, feedback) => {
    await addFeedbackMutation.mutateAsync({ id, rating, feedback });
  };

  return (
    <div className="container mx-auto">
      <HeadLayout
        title={`${t("tickets.ticket", "Ticket")} #${ticket.ticketNumber}`}
        showBackButton
        onBack={() => navigate("/tickets")}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 py-4">
        {/* Main Content - Messages */}
        <div className="lg:col-span-2 space-y-6">
          {/* Ticket Header */}
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <h1 className="text-xl font-semibold">{ticket.title}</h1>
                    <Badge className={getCategoryColor(ticket.category)}>
                      {t(
                        `tickets.category.${ticket.category}`,
                        ticket.category
                      )}
                    </Badge>
                  </div>
                  <p className="text-muted-foreground">{ticket.description}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={getStatusColor(ticket.status)}>
                    {t(`tickets.status.${ticket.status}`, ticket.status)}
                  </Badge>
                </div>
              </div>
            </CardHeader>
          </Card>

          {/* Messages */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageCircle className="h-5 w-5" />
                {t("tickets.conversation", "Conversation")} (
                {ticket.messages?.length || 0})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="max-h-[600px] overflow-y-auto space-y-4 p-4 border rounded-lg bg-muted/30">
                {ticket.messages && ticket.messages.length > 0 ? (
                  <>
                    {ticket.messages.map((message) => (
                      <MessageCard
                        key={message._id}
                        message={message}
                        isCurrentUser={message.sender._id === user._id}
                        onDownloadAttachment={handleDownloadAttachment}
                      />
                    ))}
                    <div ref={messagesEndRef} />
                  </>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    {t(
                      "tickets.noMessages",
                      "No messages yet. Start the conversation!"
                    )}
                  </div>
                )}
              </div>

              {/* Message Input */}
              {ticket.status !== "closed" && (
                <div className="space-y-4">
                  <Separator />

                  {canAddInternalNote && (
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="internal-note"
                        checked={isInternal}
                        onChange={(e) => setIsInternal(e.target.checked)}
                        className="h-4 w-4"
                      />
                      <Label htmlFor="internal-note" className="text-sm">
                        {t(
                          "tickets.internalNote",
                          "Internal note (visible to staff only)"
                        )}
                      </Label>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Textarea
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder={t(
                        "tickets.typeMessage",
                        "Type your message..."
                      )}
                      className="min-h-[100px]"
                    />

                    {attachments.length > 0 && (
                      <div className="space-y-2">
                        {attachments.map((file, index) => (
                          <div
                            key={index}
                            className="flex items-center gap-2 p-2 bg-muted rounded"
                          >
                            <Paperclip className="h-4 w-4" />
                            <span className="flex-1 text-sm truncate">
                              {file.name}
                            </span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeAttachment(index)}
                            >
                              <XCircle className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileSelect}
                        multiple
                        className="hidden"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        {t("common.attachFiles", "Attach Files")}
                      </Button>
                    </div>

                    <Button
                      onClick={handleSendMessage}
                      disabled={
                        addMessageMutation.isPending ||
                        (!newMessage.trim() && attachments.length === 0)
                      }
                    >
                      {addMessageMutation.isPending ? (
                        <ThreeDotLoader />
                      ) : (
                        <>
                          <Send className="h-4 w-4 mr-2" />
                          {t("common.send", "Send")}
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Customer Feedback */}
          {canProvideFeedback && (
            <FeedbackDialog
              ticket={ticket}
              onSubmitFeedback={handleProvideFeedback}
              isSubmitting={addFeedbackMutation.isPending}
            />
          )}
        </div>

        {/* Sidebar - Ticket Details */}
        <div className="space-y-6">
          {/* Ticket Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5" />
                {t("tickets.ticketInfo", "Ticket Information")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium">
                    {t("tickets.ticketNumber", "Ticket #")}
                  </span>
                  <p className="text-muted-foreground">{ticket.ticketNumber}</p>
                </div>
                <div>
                  <span className="font-medium">
                    {t("common.status", "Status")}
                  </span>
                  <div className="text-muted-foreground">
                    <Badge className={getStatusColor(ticket.status)}>
                      {t(`tickets.status.${ticket.status}`, ticket.status)}
                    </Badge>
                  </div>
                </div>
                <div>
                  <span className="font-medium">
                    {t("common.category", "Category")}
                  </span>
                  <div className="text-muted-foreground">
                    <Badge className={getCategoryColor(ticket.category)}>
                      {t(
                        `tickets.category.${ticket.category}`,
                        ticket.category
                      )}
                    </Badge>
                  </div>
                </div>
                <div>
                  <span className="font-medium">
                    {t("tickets.createdAt", "Created")}
                  </span>
                  <p className="text-muted-foreground">
                    {format(new Date(ticket.createdAt), "MMM dd, yyyy HH:mm")}
                  </p>
                </div>
                <div>
                  <span className="font-medium">
                    {t("tickets.updatedAt", "Updated")}
                  </span>
                  <p className="text-muted-foreground">
                    {format(new Date(ticket.updatedAt), "MMM dd, yyyy HH:mm")}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Creator Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                {t("tickets.createdBy", "Created By")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3">
                <Avatar>
                  <AvatarFallback>
                    {getInitials(
                      ticket.creator?.fullName,
                      ticket.creator?.email
                    )}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-medium">
                    {ticket.creator?.fullName || ticket.creator?.email}
                  </p>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="text-xs">
                      {t(`role.${ticket.creator?.role}`, ticket.creator?.role)}
                    </Badge>
                    {ticket.creator?.email && (
                      <span className="text-xs text-muted-foreground">
                        {ticket.creator.email}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Assignment Information */}
          {ticket.assignedTo && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <UserPlus className="h-5 w-5" />
                  {t("tickets.assignedTo", "Assigned To")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-3">
                  <Avatar>
                    <AvatarFallback>
                      {getInitials(
                        ticket.assignedTo?.fullName,
                        ticket.assignedTo?.email
                      )}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium">
                      {ticket.assignedTo?.fullName || ticket.assignedTo?.email}
                    </p>
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="text-xs">
                        {t(
                          `role.${ticket.assignedTo?.role}`,
                          ticket.assignedTo?.role
                        )}
                      </Badge>
                      {ticket.assignedTo?.email && (
                        <span className="text-xs text-muted-foreground">
                          {ticket.assignedTo.email}
                        </span>
                      )}
                    </div>
                    {ticket.assignedAt && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {t("tickets.assignedAt", "Assigned at")}:{" "}
                        {format(
                          new Date(ticket.assignedAt),
                          "MMM dd, yyyy HH:mm"
                        )}
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Related Package/Pickup */}
          {(ticket.relatedPackage || ticket.relatedPickup) && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  {ticket.relatedPackage ? (
                    <Package className="h-5 w-5" />
                  ) : (
                    <Truck className="h-5 w-5" />
                  )}
                  {t("tickets.relatedItem", "Related Item")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {ticket.relatedPackage && (
                  <div className="space-y-2">
                    <p className="font-medium">
                      {t("tickets.package", "Package")}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {ticket.relatedPackage.trackingNumber ||
                        ticket.relatedPackage._id}
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        navigate(`/packages/${ticket.relatedPackage._id}/view`)
                      }
                    >
                      {t("common.viewDetails", "View Details")}
                    </Button>
                  </div>
                )}

                {ticket.relatedPickup && (
                  <div className="space-y-2">
                    <p className="font-medium">
                      {t("tickets.pickup", "Pickup")}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {ticket.relatedPickup.name}
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        navigate(`/pickups/${ticket.relatedPickup._id}`)
                      }
                    >
                      {t("common.viewDetails", "View Details")}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Management Actions for Admin/Manager */}
          {canManageTicket && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Edit3 className="h-5 w-5" />
                  {t("tickets.management", "Ticket Management")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <TicketManagementActions ticket={ticket} onUpdate={refetch} />
              </CardContent>
            </Card>
          )}
          {ticket.resolution && (
            <Card className="border-l-4 border-l-green-500">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  {t("tickets.resolution", "Resolution")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm">{ticket.resolution}</p>
                {ticket.resolvedAt && (
                  <p className="text-xs text-muted-foreground mt-2">
                    {t("tickets.resolvedAt", "Resolved at")}:{" "}
                    {format(new Date(ticket.resolvedAt), "MMM dd, yyyy HH:mm")}
                  </p>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default TicketDetails;
